import AXIOS_INSTANCE from '@/src/api/axiosInstance'
import { useLab } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { labOrderFormSchema, type LabOrderFormValues } from '@schema/labOrder.schema'
import { GeneralResMock } from '@type/general.type'
import type {
  LabOrderData,
  LabOrderIncludedOrder,
  LabOrderIncludedResource,
  LabOrderUpdatePayload
} from '@type/lab.type'
import { LaboratoryResMock } from '@type/laboratory.type'
import type { OrderLabTestIncluded } from '@type/order.type'
import { FormButton, Modal, TextAreaField } from '@ui'
import { Select } from '@ui/SearchInput/Select'
import { getError, showToast } from '@util'
import { useCallback, useEffect, useMemo } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import type { GroupBase } from 'react-select'

type Props = {
  onClose: () => void
  id: string
  defaultData?: LabOrderData
  included?: LabOrderIncludedResource[]
}

type LabTestOption = { label: string; value: number | string }

async function searchLabTests(inputValue: string): Promise<LabTestOption[]> {
  const q = new URLSearchParams({
    'page[number]': '1',
    'page[size]': '50',
    sort_by: 'name',
    sort_dir: 'asc'
  })
  const trimmed = inputValue.trim()
  if (trimmed) q.set('query', trimmed)

  const { data } = await AXIOS_INSTANCE.get<GeneralResMock<LaboratoryResMock[]>>(`/lab_tests?${q.toString()}`)
  const rows = Array.isArray(data.data) ? data.data : []
  return rows.map(lab => ({
    label: lab.attributes.name,
    value: Number(lab.id)
  }))
}

function testsFromOrder(order: LabOrderData, included: LabOrderIncludedResource[] | undefined): LabTestOption[] {
  const orderRefs = order.relationships.orders?.data ?? []
  const remainingNames = (order.attributes.test_names ?? [])
    .map(name => name?.trim())
    .filter((label): label is string => !!label)

  const catalogTests: LabTestOption[] = orderRefs.map((ref, i) => {
    const child = included?.find((x): x is LabOrderIncludedOrder => x.type === 'order' && x.id === ref.id)
    const labTestId = child?.attributes.orderable_id != null ? Number(child.attributes.orderable_id) : NaN
    const labTest = Number.isFinite(labTestId)
      ? included?.find((x): x is OrderLabTestIncluded => x.type === 'lab_test' && String(x.id) === String(labTestId))
      : undefined
    const label =
      labTest?.attributes.name?.trim() || child?.attributes.name?.trim() || remainingNames[i] || `Test ${i + 1}`

    if (labTest?.attributes.name?.trim()) {
      const idx = remainingNames.findIndex(name => name.toLowerCase() === label.trim().toLowerCase())
      if (idx >= 0) remainingNames.splice(idx, 1)
    } else if (remainingNames.length) {
      remainingNames.shift()
    }

    if (Number.isFinite(labTestId)) {
      return { label, value: labTestId }
    }
    return { label, value: label }
  })

  const freeTextTests = remainingNames.map(label => ({ label, value: label }))
  return [...catalogTests, ...freeTextTests]
}

const EditLabOrder = ({ onClose, id, defaultData, included }: Props) => {
  const { singleLabOrder, updateLabOrder } = useLab({ id })
  const order = (singleLabOrder?.data?.type === 'lab_order' ? singleLabOrder.data : defaultData) as
    | LabOrderData
    | undefined
  const resolvedIncluded = (singleLabOrder?.included ?? included) as LabOrderIncludedResource[] | undefined

  const defaultTests = useMemo(() => (order ? testsFromOrder(order, resolvedIncluded) : []), [order, resolvedIncluded])

  const methods = useForm<LabOrderFormValues>({
    defaultValues: { lab_tests: [], note: order?.attributes.note ?? '' },
    resolver: yupResolver(labOrderFormSchema)
  })
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting }
  } = methods

  useEffect(() => {
    if (!order) return
    reset({
      lab_tests: defaultTests,
      note: order.attributes.note ?? ''
    })
  }, [defaultTests, order, reset])

  const loadLabTestOptions = useCallback((inputValue: string) => searchLabTests(inputValue), [])

  const onSubmit = async (form: LabOrderFormValues) => {
    if (!id) return
    if (String(order?.attributes.status ?? '').toLowerCase() !== 'pending') {
      showToast('Only pending lab orders can be edited')
      return
    }

    const lab_test_ids = form.lab_tests
      .map(t => t.value)
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
    const test_names = form.lab_tests
      .filter(t => typeof t.value === 'string')
      .map(t => String(t.label).trim())
      .filter(Boolean)

    const payload: LabOrderUpdatePayload = {
      data: {
        type: 'lab_order',
        attributes: {
          lab_test_ids,
          note: form.note?.trim() ?? '',
          test_names
        }
      }
    }

    try {
      await updateLabOrder({ formVal: payload, id })
      showToast('Lab order updated successfully')
      onClose()
    } catch (error) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Edit lab test">
        <form
          className="flex w-full min-w-0 max-w-[530px] flex-col gap-4 overflow-auto"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-4 overflow-auto p-6">
            <Controller
              control={control}
              name="lab_tests"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Select<LabTestOption, true, GroupBase<LabTestOption>>
                  borderRadius={'15px'}
                  defaultOptions
                  error={error?.message}
                  getNewOptionData={(inputValue, optionLabel) => ({
                    label: String(optionLabel),
                    value: String(inputValue).trim()
                  })}
                  getOptionLabel={o => o.label}
                  getOptionValue={o => String(o.value)}
                  isAsync
                  isCreatable
                  isMulti
                  label="Test"
                  loadOptions={loadLabTestOptions}
                  onChange={opts => onChange((opts as LabTestOption[] | null) ?? [])}
                  placeholder="Search and select tests"
                  value={value as LabTestOption[]}
                />
              )}
            />

            <TextAreaField label="Note to lab" name="note" placeholder="Enter Note" />
          </div>
          <FormButton isLoading={isSubmitting} onClose={onClose} submit="Save" />
        </form>
      </Modal>
    </FormProvider>
  )
}

export default EditLabOrder
